// GR-3377: grid clustering for the sourcing venue map.
//
// Our own rather than leaflet.markercluster's, for two reasons: the map costs
// exactly one new dependency instead of two, and the thing that decides what
// the user sees is a pure function we can unit test instead of a plugin that
// only exists once a real map is on screen.
//
// The grid is laid out in Web Mercator pixel space at the current zoom, which
// is what makes it behave the way a map reader expects: two hotels a block
// apart occupy the same cell when the state is in view and separate cells
// once you are down at street level, without any per-zoom tuning.

const TILE_SIZE = 256;
const DEFAULT_GRID_SIZE = 60;
const MAX_LATITUDE = 85.0511287798; // Where the Mercator projection runs off.

const usable = (value) => typeof value === "number" && Number.isFinite(value);

// Longitude/latitude to pixels at `zoom`, the same projection Leaflet uses.
function project(lat, lng, zoom) {
  const worldSize = TILE_SIZE * 2 ** zoom;
  const clamped = Math.max(Math.min(lat, MAX_LATITUDE), -MAX_LATITUDE);
  const sin = Math.sin((clamped * Math.PI) / 180);
  return {
    x: ((lng + 180) / 360) * worldSize,
    y: (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * worldSize,
  };
}

/**
 * Group points into clusters for the given zoom.
 *
 * @param {Array<{lat: number, lng: number}>} points venues to place
 * @param {{zoom: number, gridSize?: number}} options grid cell size in pixels
 * @returns {Array<{lat: number, lng: number, count: number, points: Array}>}
 *   one entry per marker to draw, in the order the points arrived. A `count`
 *   of 1 is a single venue and keeps its exact position; anything more sits
 *   at the mean of its members.
 */
export function clusterPoints(points, { zoom, gridSize = DEFAULT_GRID_SIZE } = {}) {
  const cells = new Map();

  (points || []).forEach((point) => {
    const lat = Number(point?.lat);
    const lng = Number(point?.lng);
    // A venue with no coordinates is not a point on a map; dropping it beats
    // stacking the directory's unmapped rows off the coast of Ghana.
    if (!usable(lat) || !usable(lng) || point?.lat === null || point?.lng === null) return;

    const { x, y } = project(lat, lng, zoom);
    const key = `${Math.floor(x / gridSize)}:${Math.floor(y / gridSize)}`;
    const cell = cells.get(key);
    if (cell) {
      cell.points.push(point);
      cell.latSum += lat;
      cell.lngSum += lng;
    } else {
      cells.set(key, { points: [point], latSum: lat, lngSum: lng });
    }
  });

  return Array.from(cells.values(), ({ points: members, latSum, lngSum }) => {
    if (members.length === 1) {
      return { lat: members[0].lat, lng: members[0].lng, count: 1, points: members };
    }
    return {
      lat: latSum / members.length,
      lng: lngSum / members.length,
      count: members.length,
      points: members,
    };
  });
}
